import React, {useEffect} from "react";
import {useParams, Link} from "react-router-dom";
import "./ProjectDetail.scss";
import projects from "../../data/projects";

// Accepts either a Google Drive share URL or a raw file ID; returns the embed URL.
// Returns null if the input is empty/placeholder.
function googleDriveEmbedUrl(raw) {
  if (!raw || raw.startsWith("REPLACE_")) return null;
  const match = raw.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  const fileId = match ? match[1] : raw.trim();
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

export default function ProjectDetail() {
  const {slug} = useParams();
  const project = projects[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!project) {
    return (
      <div className="case-study dark-mode">
        <div className="case-study-inner">
          <Link to="/" className="back-link">← Back to portfolio</Link>
          <h1>Project not found</h1>
          <p>No case study exists at <code>/projects/{slug}</code>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="case-study dark-mode">
      <div className="case-study-inner">
        <Link to="/" className="back-link">← Back to portfolio</Link>

        <header className="project-hero">
          {project.logo && (
            <img src={project.logo} alt={`${project.name} logo`} className="project-hero-logo" />
          )}
          <div className="project-hero-text">
            <p className="project-hero-eyebrow">{project.period} · {project.status}</p>
            <h1>{project.name}</h1>
            <p className="project-hero-tagline">{project.tagline}</p>
            <p className="project-hero-role"><strong>Role:</strong> {project.role}</p>
            {project.links && project.links.length > 0 && (
              <div className="project-hero-links">
                {project.links.map((l, i) => (
                  <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="project-hero-link">
                    {l.label} ↗
                  </a>
                ))}
              </div>
            )}
          </div>
        </header>

        {project.video && googleDriveEmbedUrl(project.video.url) && (
          <section className="project-video">
            <h2>Demo video</h2>
            <div className="project-video-frame">
              <iframe
                src={googleDriveEmbedUrl(project.video.url)}
                title={project.video.caption || `${project.name} demo video`}
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
            {project.video.caption && (
              <p className="project-video-caption">{project.video.caption}</p>
            )}
          </section>
        )}

        <section className="project-tech">
          <h2>Tech stack</h2>
          <div className="project-tech-tags">
            {project.techStack.map((t, i) => (
              <span key={i} className="project-tech-tag">{t}</span>
            ))}
          </div>
        </section>

        {project.sections.map((s, i) => {
          const isReflection = /learn|reflect/i.test(s.heading);
          return (
            <section key={i} className={isReflection ? "project-section reflection" : "project-section"}>
              <h2>{s.heading}</h2>
              <p>{s.body}</p>
            </section>
          );
        })}

        {project.screenshots && project.screenshots.length > 0 && (
          <section className="project-screenshots">
            <h2>Screenshots</h2>
            <div className="screenshot-grid">
              {project.screenshots.map((s, i) => (
                <figure key={i} className="screenshot">
                  <img src={s.src} alt={s.caption || `${project.name} screenshot ${i + 1}`} />
                  {s.caption && <figcaption>{s.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </section>
        )}

        <footer className="case-study-footer">
          <Link to="/" className="back-link">← Back to portfolio</Link>
        </footer>
      </div>
    </div>
  );
}
